import React from 'react';
import { useFlowContext } from '../flow/FlowProvider';
import { isConnectedBlockType } from '../flow/connectedBlocks';
import { ConnectedBlockSurface } from '../flow/ConnectedBlockSurface';
import RequestFormSurface from '../flow/RequestFormSurface';
import ModelPreviewModal from '../ModelPreviewModal';
import ProgressIndicator from '../ProgressIndicator';
import { useProductRecords } from '../../hooks/useProductRecords';
import type { EnquiryFormData, SelectedVase } from '../../types';
import { REQUEST_FLOW_FIELD_IDS } from '../../lib/flowFieldExtensions';
import { VASE_FLOW_FIELD_IDS } from '../../lib/flows';
import { requestItemsToSelectedVases } from '../../lib/flowNodeRuntime';
import {
  resolveProductCardSelectionTarget,
  resolveSectionOrPageFlow,
} from '../../lib/flowBlockResolution';
import { resolveProductCardSelectActionTargets } from '../../lib/productCardEventBridge';
import {
  resolveProductRecordModelPath,
  resolveProductRecordTitle,
  resolveProductSelectionPayload,
} from '../../lib/productRecords';
import { resolveFooterContent } from '../../lib/footerContent';
import {
  resolveStepTrackerActiveStep,
  resolveStepTrackerContent,
} from '../../lib/progressContent';
import {
  resolveSectionMinHeightPx,
} from './layoutEngine/sectionSizing';
import ProductGalleryBlockSurface from './ProductGalleryBlockSurface';
import Footer from './Footer';
import { resolveFlowSectionBlocks } from './flowSectionSupport';
import type { HomepageSectionSettings } from './sectionRegistry';

interface FlowSectionProps {
  sectionId?: string;
  settings?: HomepageSectionSettings;
  enquiryEmailTo?: string;
  onSelectVase?: (vase: SelectedVase) => void;
  onEnquirySubmit?: (formData: EnquiryFormData) => void;
}

const FLOW_SECTION_FLOOR_ROWS = 6;
const FLOW_SECTION_DEFAULT_ROWS = 12;

function clamp(value: number, min: number, max: number) {
  return Math.max(min, Math.min(max, value));
}

function resolveFlowBlockColumnSpan(width: number) {
  return clamp(Math.round(width / 2), 1, 12);
}

function getFlowBlockColumnClass(span: number) {
  switch (span) {
    case 1:
      return 'xl:col-span-1';
    case 2:
      return 'xl:col-span-2';
    case 3:
      return 'xl:col-span-3';
    case 4:
      return 'xl:col-span-4';
    case 5:
      return 'xl:col-span-5';
    case 6:
      return 'xl:col-span-6';
    case 7:
      return 'xl:col-span-7';
    case 8:
      return 'xl:col-span-8';
    case 9:
      return 'xl:col-span-9';
    case 10:
      return 'xl:col-span-10';
    case 11:
      return 'xl:col-span-11';
    case 12:
    default:
      return 'xl:col-span-12';
  }
}

const FlowSection: React.FC<FlowSectionProps> = ({
  sectionId,
  settings,
  enquiryEmailTo,
  onSelectVase,
  onEnquirySubmit,
}) => {
  const { products: productRecords } = useProductRecords();
  const flow = useFlowContext();
  const flowBlocks = React.useMemo(() => resolveFlowSectionBlocks(settings), [settings]);
  const minHeight = React.useMemo(
    () =>
      resolveSectionMinHeightPx(settings, {
        floorRows: FLOW_SECTION_FLOOR_ROWS,
        fallbackRows: FLOW_SECTION_DEFAULT_ROWS,
      }),
    [settings]
  );
  const [activeModelPreview, setActiveModelPreview] = React.useState<{
    modelPath: string;
    vaseId?: number;
    title?: string;
  } | null>(null);

  const selectedProductIds = React.useMemo(() => {
    const activeFlow = resolveSectionOrPageFlow(flow.flows, sectionId ?? null);
    if (!activeFlow) return new Set<number>();

    const requestSelectedVases = requestItemsToSelectedVases(
      flow.getFlowValue(activeFlow.id, REQUEST_FLOW_FIELD_IDS.requestItems)
    );
    if (requestSelectedVases.length > 0) {
      return new Set(
        requestSelectedVases
          .map((product) => product.id)
          .filter((productId) => Number.isFinite(productId))
      );
    }

    const selectedProduct = flow.getFlowValue(activeFlow.id, VASE_FLOW_FIELD_IDS.chosenProduct);
    return selectedProduct &&
      typeof selectedProduct === 'object' &&
      Number.isFinite((selectedProduct as { id?: unknown }).id)
      ? new Set([Number((selectedProduct as { id: number }).id)])
      : new Set<number>();
  }, [flow, sectionId]);

  const handleSelectProduct = React.useCallback(
    (blockId: string, product: any) => {
      const selectedVase = resolveProductSelectionPayload(product);
      if (!selectedVase) return;
      const target = resolveProductCardSelectionTarget(flow.flows, {
        block: {
          id: blockId,
          type: 'gallery',
          layout: { x: 1, y: 1, w: 24, h: 1 },
        },
        sectionId,
        vase: selectedVase,
      });
      if (target) {
        flow.patchFlowValues(target.flowId, target.values);
      }
      const actionTargets = resolveProductCardSelectActionTargets(flow.flows, {
        blockId,
        blockType: 'gallery',
        sectionId,
      });
      if (actionTargets?.selectFieldId) {
        flow.patchFlowValues(actionTargets.flowId, {
          [actionTargets.selectFieldId]: Date.now(),
        });
      }
      actionTargets?.targetNodeIds.forEach((nodeId) => {
        flow.invokeFlowNode(actionTargets.flowId, nodeId);
      });
      onSelectVase?.(selectedVase);
    },
    [flow, onSelectVase, sectionId]
  );

  return (
    <>
      <section className="relative w-full">
        <div
          className="grid grid-cols-1 gap-6 xl:grid-cols-12"
          style={{ minHeight: `${minHeight}px` }}
        >
          {flowBlocks.map((block) => {
            const span = resolveFlowBlockColumnSpan(block.layout.w);
            const shellClassName = getFlowBlockColumnClass(span);
            const shellStyle: React.CSSProperties = {
              minHeight: `${Math.max(120, block.layout.h * 36)}px`,
            };

            if (block.type === 'request-form') {
              return (
                <div key={block.id} className={shellClassName} style={shellStyle}>
                  <RequestFormSurface
                    block={block}
                    sectionId={sectionId}
                    enquiryEmailTo={enquiryEmailTo}
                    onEnquirySubmit={onEnquirySubmit}
                  />
                </div>
              );
            }

            if (block.type === 'site-footer') {
              const footerContent = resolveFooterContent(block.content);
              return (
                <div key={block.id} className="xl:col-span-12">
                  <Footer settings={footerContent} />
                </div>
              );
            }

            if (block.type === 'site-progress') {
              const stepTracker = resolveStepTrackerContent(block.content, block.id);
              const activeStepTracker = resolveStepTrackerActiveStep(
                flow.flowState,
                flow.flows,
                stepTracker.trackerId
              );
              return (
                <div key={block.id} className="xl:col-span-12">
                  <ProgressIndicator
                    steps={stepTracker.steps}
                    placement="fixed"
                    activeStepId={activeStepTracker.stepId ?? stepTracker.defaultStepId}
                    statusMode={activeStepTracker.hasAction ? stepTracker.statusMode : 'current'}
                    stickySectionIds={stepTracker.stickySectionIds}
                  />
                </div>
              );
            }

            if (block.type === 'gallery') {
              return (
                <div key={block.id} className="xl:col-span-12" style={shellStyle}>
                  <ProductGalleryBlockSurface
                    content={block.content}
                    style={block.style}
                    products={productRecords}
                    selectedProductIds={selectedProductIds}
                    moveMode={false}
                    selectionOverlay={null}
                    onSelectProduct={(product) => handleSelectProduct(block.id, product)}
                    onViewModel={(product) => {
                      const modelPath = resolveProductRecordModelPath(product).trim();
                      if (!modelPath) return;
                      setActiveModelPreview({
                        modelPath,
                        vaseId:
                          Number.isFinite(product.modelId) && Number(product.modelId) > 0
                            ? Number(product.modelId)
                            : product.id,
                        title: resolveProductRecordTitle(product) || product.name,
                      });
                    }}
                  />
                </div>
              );
            }

            if (isConnectedBlockType(block.type)) {
              return (
                <div key={block.id} className={shellClassName} style={shellStyle}>
                  <ConnectedBlockSurface
                    block={block}
                    sectionId={sectionId}
                    selectionOverlay={null}
                  />
                </div>
              );
            }

            return null;
          })}
        </div>
      </section>
      <ModelPreviewModal
        open={activeModelPreview !== null}
        onClose={() => setActiveModelPreview(null)}
        modelPath={activeModelPreview?.modelPath}
        vaseId={activeModelPreview?.vaseId}
        title={activeModelPreview?.title ?? '3D model preview'}
      />
    </>
  );
};

export default FlowSection;
